import { jsPDF } from 'jspdf';
import {
  countPageMarkers,
  splitTextByPageMarkers,
  validatePageIntegrity,
  type PageIntegrityReport,
} from './pageIntegrity';

const MARGIN = 15;
const FONT_SIZE = 10;
const LINE_HEIGHT = 5;

export type PdfExportResult = {
  blob: Blob;
  /** Logical source pages found via --- Page N --- markers (0 if none). */
  logicalPageCount: number;
  /** Physical pages written by jsPDF (may exceed logical when content wraps). */
  physicalPageCount: number;
  integrity: PageIntegrityReport | null;
};

/**
 * Build a readable PDF from redacted plain text.
 * When page markers are present, each original page starts on a new PDF page
 * so export layout tracks source pages more closely than continuous reflow.
 */
export function createPdfFromText(
  text: string,
  options?: { expectedPageCount?: number }
): PdfExportResult {
  const doc = new jsPDF({ unit: 'mm', format: 'letter' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(FONT_SIZE);

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const maxWidth = pageWidth - MARGIN * 2;

  const sections = splitTextByPageMarkers(text || '(empty document)');
  const hasMarkers = sections.some((section) => section.pageNumber != null);

  let firstSection = true;
  for (const section of sections) {
    if (!firstSection && hasMarkers && section.pageNumber != null) {
      doc.addPage();
    }
    firstSection = false;

    const lines = doc.splitTextToSize(section.content, maxWidth) as string[];
    let y = MARGIN;
    for (const line of lines) {
      if (y + LINE_HEIGHT > pageHeight - MARGIN) {
        doc.addPage();
        y = MARGIN;
      }
      doc.text(line, MARGIN, y);
      y += LINE_HEIGHT;
    }
  }

  const logicalPageCount = countPageMarkers(text);
  const physicalPageCount = doc.getNumberOfPages();
  const integrity =
    options?.expectedPageCount != null && options.expectedPageCount > 0
      ? validatePageIntegrity(text, options.expectedPageCount)
      : null;

  return {
    blob: doc.output('blob'),
    logicalPageCount,
    physicalPageCount,
    integrity,
  };
}

/**
 * Build a readable PDF from redacted plain text (standard redaction export).
 */
export function createPdfBlobFromText(text: string): Blob {
  return createPdfFromText(text).blob;
}
